import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize, map, Observable, switchMap, tap } from 'rxjs';

import { TablesApiService } from '@blaze-shared/datasets-data-access';
import { BlazeTable, BlazeTableIndexes } from '@blaze-shared/datasets-domain';
import { DialogModule, SharedModule } from '@blaze-shared/shared-feature';
import {
  ButtonComponent,
  CheckboxComponent,
  safeDetectChanges,
  TooltipDirective,
} from '@blaze-shared/shared-ui';

@UntilDestroy()
@Component({
  selector: 'blaze-table-indexes-dialog',
  templateUrl: './blaze-table-indexes-dialog.component.html',
  styleUrls: ['./blaze-table-indexes-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    SharedModule,
    ButtonComponent,
    CommonModule,
    DialogModule,
    TooltipDirective,
    CheckboxComponent,
  ],
})
export class BlazeTableIndexesDialogComponent implements OnInit {
  private readonly tableService = inject(TablesApiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialogRef = inject(MatDialogRef);

  form = new FormGroup({
    columns: new FormArray<FormGroup>([]),
  });

  blazeTableConfig$: Observable<BlazeTable>;
  compoundIndexes: BlazeTableIndexes['compoundIndexes'] = [];
  columns$: Observable<string[]>;
  isListView = signal(true);
  disableAction = signal(true);
  tableId: string;

  get columnControls(): FormGroup[] {
    return (this.form.get('columns') as FormArray<FormGroup>).controls as FormGroup[];
  }

  ngOnInit(): void {
    this.blazeTableConfig$
      .pipe(
        tap((config) => {
          this.tableId = config.id;
        }),
        switchMap((config) => this.tableService.getIndexes(config.id)),
        map((indexes) => indexes.compoundIndexes),
        untilDestroyed(this),
        finalize(() => {
          this.disableAction.set(false);
        }),
      )
      .subscribe((indexes) => {
        this.disableAction.set(false);
        this.compoundIndexes = indexes;
        if (!indexes.length) {
          this.isListView.set(false);
        }
      });

    this.columns$ = this.blazeTableConfig$.pipe(
      map((config) =>
        config.columns
          .filter((column) => !column.isInline && !column.isInternal)
          .map((column) => column.name),
      ),
      tap((columns) => {
        this.updateForm(columns);
      }),
    );
  }

  create(): void {
    this.disableAction.set(true);
    const columns = this.form.value.columns
      ?.filter((c) => c.selected)
      .map(({ columnName, direction }) => ({ columnName, direction }));

    if (!columns?.length) {
      this.disableAction.set(false);
      return;
    }
    this.tableService
      .createIndex(this.tableId, columns)
      .pipe(
        untilDestroyed(this),
        finalize(() => {
          this.disableAction.set(false);
          this.isListView.set(true);
        }),
      )
      .subscribe((indexes) => {
        this.compoundIndexes = indexes.compoundIndexes;
      });
  }

  switchDirection(index: number): void {
    const direction = this.columnControls[index].controls['direction'];
    direction.setValue(direction.value === 'Ascending' ? 'Descending' : 'Ascending');
  }

  deleteIndex(indexName: string): void {
    this.tableService
      .deleteIndex(this.tableId, indexName)
      .pipe(untilDestroyed(this))
      .subscribe((indexes) => {
        this.compoundIndexes = indexes.compoundIndexes;
        safeDetectChanges(this.cdr);
      });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  private updateForm(columns: string[]): void {
    this.form = new FormGroup({
      columns: new FormArray<FormGroup>([]),
    });
    columns.forEach((column) => {
      const columnsFormGroup = new FormGroup({});
      columnsFormGroup.addControl('selected', new FormControl(false));
      columnsFormGroup.addControl('columnName', new FormControl(column));
      columnsFormGroup.addControl(
        'direction',
        new FormControl<'Descending' | 'Ascending'>('Ascending'),
      );
      (this.form.get('columns') as FormArray<FormGroup>).push(columnsFormGroup);
    });
  }
}
